import { useEffect, useState } from 'react'
import { useAdmin, type PlatformStorageData } from '@/hooks/useAdmin'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { formatBytes } from '@/lib/formatBytes'
import { HardDrive, Database } from 'lucide-react'

export function StoragePage() {
  const { getPlatformStorage } = useAdmin()
  const [data, setData] = useState<PlatformStorageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    getPlatformStorage()
      .then((d) => setData(d))
      .catch(() => setError('Failed to load storage data'))
      .finally(() => setLoading(false))
  }, [getPlatformStorage])

  const pct =
    data?.capacityBytes && data.capacityBytes > 0
      ? Math.min(100, (data.physicalDatabaseBytes / data.capacityBytes) * 100)
      : null
  const danger = pct !== null && pct >= 80

  return (
    <div className="space-y-6">
      <div>
        <h1 className="brand-wordmark text-2xl">Storage</h1>
        <p className="kicker text-muted-foreground mt-1">
          Platform-wide database storage across all accounts
        </p>
      </div>

      {error && (
        <div
          className="rounded-lg p-3 text-sm border"
          style={{
            background: 'hsl(var(--destructive) / 0.1)',
            borderColor: 'hsl(var(--destructive) / 0.2)',
            color: 'hsl(var(--destructive))',
          }}
        >
          {error}
        </div>
      )}

      {loading && !error && (
        <Card>
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-4 rounded bg-muted/40 animate-pulse" style={{ width: `${70 + i * 5}%` }} />
            ))}
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* Summary metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Physical DB size</span>
                  <Database className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="text-2xl font-bold">{formatBytes(data.physicalDatabaseBytes)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Logical (sum of accounts)</span>
                  <HardDrive className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="text-2xl font-bold">{formatBytes(data.grandTotalLogicalBytes)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Accounts</span>
                </div>
                <div className="text-2xl font-bold">{data.accounts.length}</div>
              </CardContent>
            </Card>
          </div>

          {/* Headroom indicator */}
          {pct !== null && data.capacityBytes && (
            <Card>
              <CardContent className="p-5">
                <div className="mb-2 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground font-medium">Database headroom</span>
                  <span className={danger ? 'font-semibold text-destructive' : 'text-muted-foreground'}>
                    {formatBytes(data.physicalDatabaseBytes)} of {formatBytes(data.capacityBytes)} — {pct.toFixed(1)}%
                  </span>
                </div>
                <Progress value={pct} className={danger ? '[&>div]:bg-destructive' : undefined} />
                <p className="text-[11px] text-muted-foreground/70 mt-2">
                  Physical includes indexes, bloat, and WAL that belong to no single account, so it
                  exceeds the sum of accounts. A large gap is a signal to VACUUM or right-size.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Per-account table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Per-account breakdown</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="py-2.5 px-6 font-medium">Account</th>
                      <th className="py-2.5 pr-4 font-medium">Tier</th>
                      <th className="py-2.5 pr-4 text-right font-medium">Total</th>
                      <th className="py-2.5 pr-4 text-right font-medium">Comparisons</th>
                      <th className="py-2.5 pr-4 text-right font-medium">Snapshots</th>
                      <th className="py-2.5 pr-4 text-right font-medium">Archives</th>
                      <th className="py-2.5 pr-4 text-right font-medium">Data ops</th>
                      <th className="py-2.5 px-6 text-right font-medium">Logs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.accounts.map((a) => (
                      <tr key={a.organizationId} className="border-b last:border-0 hover:bg-accent/20 transition-colors">
                        <td className="py-3 px-6">
                          <div className="font-medium text-foreground">{a.name}</div>
                          <div className="mono-accent text-muted-foreground">{a.organizationId}</div>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge variant="outline" className="text-xs capitalize">{a.tier}</Badge>
                        </td>
                        <td className="py-3 pr-4 text-right font-mono font-semibold">{formatBytes(a.totalBytes)}</td>
                        <td className="py-3 pr-4 text-right font-mono text-muted-foreground">{formatBytes(a.buckets.comparisons)}</td>
                        <td className="py-3 pr-4 text-right font-mono text-muted-foreground">{formatBytes(a.buckets.snapshots)}</td>
                        <td className="py-3 pr-4 text-right font-mono text-muted-foreground">{formatBytes(a.buckets.archives)}</td>
                        <td className="py-3 pr-4 text-right font-mono text-muted-foreground">{formatBytes(a.buckets.dataOps)}</td>
                        <td className="py-3 px-6 text-right font-mono text-muted-foreground">{formatBytes(a.buckets.logs)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Top tables by physical size */}
          {data.perTablePhysical.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top tables by physical size</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="py-2.5 px-6 font-medium">Table</th>
                        <th className="py-2.5 px-6 text-right font-medium">Physical size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.perTablePhysical.slice(0, 15).map((t) => (
                        <tr key={t.table} className="border-b last:border-0 hover:bg-accent/20 transition-colors">
                          <td className="py-2.5 px-6 font-mono text-muted-foreground">{t.table}</td>
                          <td className="py-2.5 px-6 text-right font-mono">{formatBytes(t.bytes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          <p className="text-[11px] text-muted-foreground/60">
            Computed at {new Date(data.computedAt).toLocaleString()}
          </p>
        </>
      )}
    </div>
  )
}
